import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import type { StartList, StartListEntry } from '../../types/startList';
import { useReorderStartListEntry } from '../../api/hooks/useStartList';

interface Props {
  startList: StartList;
  /** Pokud false → pouze cteni (verejny pohled). */
  editable?: boolean;
}

interface DragState {
  entryId: number;
  fromHeat: number;
  fromLane: number;
}

/**
 * Tabulka rozbehu × drah s native HTML5 drag&drop pro presun atletu mezi bunkami.
 * Pokud cilova bunka je obsazena, backend swapne atlety.
 */
export function StartListHeatsView({ startList, editable = true }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const reorderMut = useReorderStartListEntry(startList.groupId);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const laneHeaders = useMemo(
    () => Array.from({ length: startList.parallelLanes }, (_, i) => i + 1),
    [startList.parallelLanes],
  );

  const handleDragStart = (entry: StartListEntry) => {
    if (!editable) return;
    setDragState({
      entryId: entry.id,
      fromHeat: entry.heatNumber,
      fromLane: entry.laneNumber,
    });
  };

  const handleDrop = (toHeat: number, toLane: number) => {
    if (!editable || !dragState) return;
    if (dragState.fromHeat === toHeat && dragState.fromLane === toLane) {
      setDragState(null);
      return;
    }
    reorderMut.mutate(
      {
        entryId: dragState.entryId,
        req: { heatNumber: toHeat, laneNumber: toLane },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Pozice upravena', { variant: 'success' });
          setDragState(null);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { message?: string } } };
          enqueueSnackbar(e?.response?.data?.message ?? 'Chyba pri presunu', { variant: 'error' });
          setDragState(null);
        },
      },
    );
  };

  if (startList.heats.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Startovka jeste neni vygenerovana. Klikne na tlacitko „Vygenerovat startovku".
      </Alert>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700, width: 80 }}>Rozběh</TableCell>
              {laneHeaders.map((lane) => (
                <TableCell key={lane} sx={{ fontWeight: 700 }}>
                  Dráha {lane}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {startList.heats.map((heat) => (
              <TableRow key={heat.heatNumber} hover>
                <TableCell sx={{ fontWeight: 700 }}>{heat.heatNumber}.</TableCell>
                {laneHeaders.map((lane) => {
                  const entry = heat.lanes[lane - 1] ?? null;
                  return (
                    <TableCell
                      key={lane}
                      sx={{
                        verticalAlign: 'top',
                        bgcolor: entry == null ? 'action.disabledBackground' : 'background.paper',
                        cursor: editable ? 'pointer' : 'default',
                        userSelect: 'none',
                        minWidth: 200,
                      }}
                      onDragOver={(e) => {
                        if (!editable) return;
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(heat.heatNumber, lane);
                      }}
                    >
                      {entry ? (
                        <Box
                          draggable={editable}
                          onDragStart={() => handleDragStart(entry)}
                          sx={{
                            cursor: editable ? 'grab' : 'default',
                            '&:active': { cursor: editable ? 'grabbing' : 'default' },
                          }}
                        >
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              {entry.startNumber != null && (
                                <Chip
                                  size="small"
                                  label={entry.startNumber}
                                  color="primary"
                                  sx={{ height: 20, mr: 0.5, fontWeight: 700 }}
                                />
                              )}
                              {entry.athleteName ?? entry.individualEntryName ?? '—'}
                            </Typography>
                            {(entry.teamName || entry.sdhUnitName) && (
                              <Typography variant="caption" color="text.secondary">
                                {entry.teamName ?? entry.sdhUnitName}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          (volna draha)
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
